import React, {
  useState,
  useLayoutEffect,
  useCallback,
  useEffect,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import { colors } from "src/assets";
import { UserCard, CallOffer, Typography, Chat, Button } from "src/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import {
  useUser,
  useCognito,
  useCall,
  useChatMessages,
  useChatMessagesActions,
  usePreOffer,
  useOffer,
  usePreOfferActions,
  useChatUnreadMessages,
} from "src/hooks";
import { v4 as uuid } from "uuid";
import { RequestStatus } from "src/domains";
import { CallChatCloseEvent } from "src/utils/event";
import { IConversationMessage, IConversationMessageInput } from "src/domains";
import { RootStackScreenProps } from "src/navigation/types";

export type PreOfferChatNavigationParam = {
  preOfferId?: string;
  offerId?: string;
  isOwner?: boolean;
};

export const PreOfferChatScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"PreOfferChat">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"PreOfferChat">["route"]>();
  const [preOfferId, setPreOfferId] = useState<string | undefined>(
    route.params.preOfferId
  );
  const [cognitoUsername] = useCognito();
  const [preOffer] = usePreOffer(preOfferId);
  const [offer] = useOffer(route.params.offerId);
  const [user] = useUser(
    preOffer
      ? preOffer?.senderId === cognitoUsername
        ? preOffer?.opponentId
        : preOffer?.senderId
      : offer?.ownerId
  );
  const { createPreOffer } = usePreOfferActions();

  useEffect(() => {
    setPreOfferId(route.params.preOfferId);
  }, [route.params.preOfferId]);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: preOffer?.service?.title
        ? preOffer?.service?.title
        : offer?.title
        ? offer.title
        : "",
      headerRight: () => (
        <Button
          onPress={() => {
            if (preOffer?.service?.id) {
              navigation.navigate("Offer", { offerId: preOffer?.service?.id });
            } else if (offer?.id) {
              navigation.navigate("Offer", { offerId: offer?.id });
            }
          }}
          variant={"icon"}
          title="詳細"
          iconSize={24}
          iconColor={"black"}
          containerStyle={styles.headerButtonRight}
        />
      ),
    });
  }, [navigation, offer, preOffer]);

  const [messages] = useChatMessages(preOfferId);
  const [sendingMessages, setSendingMessages] = useState<
    Array<IConversationMessage>
  >([]);
  const { conversationMessage, conversationReadMessage } =
    useChatMessagesActions();
  const [unreadMessages, , , refreshUnreadMessages] = useChatUnreadMessages();

  useEffect(() => {
    if (preOfferId) {
      unreadMessages
        .filter((message) => message.conversationId === preOfferId)
        .forEach((message) => conversationReadMessage({ id: message.id }));
    }
  }, [unreadMessages, preOfferId]);

  useEffect(() => {
    refreshUnreadMessages();
  }, [messages, refreshUnreadMessages]);

  useEffect(() => {
    setSendingMessages((a) =>
      a.filter((b) => !messages.find((c) => c.id === b.id))
    );
  }, [messages]);

  const sendHandler = useCallback(
    async ({ text }: { text?: string }) => {
      if (!preOffer) {
        if (!route.params.offerId || !offer || !offer.ownerId) {
          return;
        }
        let localPreOfferId = uuid() as string;
        const input: IConversationMessageInput = {
          id: uuid() as string,
          conversationId: localPreOfferId,
          opponentId: offer.ownerId,
          text: text,
        };
        setSendingMessages((a) => [
          ...a,
          {
            id: input.id,
            conversationId: input.conversationId,
            ownerId: cognitoUsername || "unknown",
            opponentId: input.opponentId,
            content: {
              text: input.text,
            },
          },
        ]);
        await createPreOffer({
          input: {
            id: localPreOfferId,
            opponentId: offer.ownerId,
            serviceId: route.params.offerId,
          },
        });
        setPreOfferId(localPreOfferId);

        await conversationMessage({
          input,
          type: "pre-offer",
        });
      } else {
        const input: IConversationMessageInput = {
          id: uuid() as string,
          conversationId: preOffer.id,
          opponentId:
            preOffer?.senderId === cognitoUsername
              ? preOffer?.opponentId
              : preOffer?.senderId,
          text: text,
        };
        setSendingMessages((a) => [
          {
            id: input.id,
            conversationId: input.conversationId,
            ownerId: cognitoUsername || "unknown",
            opponentId: input.opponentId,
            content: {
              text: input.text,
            },
          },
          ...a,
        ]);
        await conversationMessage({
          input,
          type: "pre-offer",
        });
      }
    },
    [preOffer, route, offer]
  );

  const onUserHandler = useCallback(() => {
    if (user) {
      if (preOffer?.senderId === cognitoUsername || route.params.isOwner) {
        navigation.push("Owner", { id: user.id });
      } else {
        navigation.push("User", { id: user.id, title: "" });
      }
    }
  }, [navigation, user, preOffer, cognitoUsername]);

  return (
    <View style={styles.root}>
      <View style={styles.content}>
        {user ? (
          <UserCard
            contanerStyle={styles.user}
            source={user?.image ? { uri: user?.image } : undefined}
            status={user?.status}
            name={user?.name}
            birthday={user?.birthday}
            gender={user?.gender}
            prefecture={user?.address?.prefecture}
            isVerified={user?.identificationStatus === "APPROVED"}
            onPress={onUserHandler}
          />
        ) : null}
        {cognitoUsername && user ? (
          <Chat
            containerStyle={styles.chat}
            userId={cognitoUsername}
            messages={[...sendingMessages, ...messages]}
            onSend={sendHandler}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.chokuGray11,
  },
  content: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  chat: {
    flex: 1,
  },
  user: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  headerButtonRight: {
    marginRight: 16,
  },
});
